import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Check,
  ExternalLink,
  RefreshCw,
  X,
  Search,
  Eye,
  Maximize2,
  Sparkles,
  ShoppingBag,
  CheckCircle2,
  Package,
  Info,
  Zap,
  Globe,
} from 'lucide-react';
import {
  decideEnrichment,
  deleteEnrichmentProposal,
  fetchEnrichmentProductProposals,
  fetchEnrichmentProposals,
  triggerEnrichmentCrawl,
  liveLookupEnrichment,
  type EnrichmentProposal,
  type LiveLookupResult,
  type LiveLookupUnlinkedRecord,
  toTitleCase,
} from '../api.js';
import {
  CreateWooDraftModal,
  type CreateWooDraftInitialData,
  type WooDraftExtraOptions,
} from '../components/CreateWooDraftModal.js';
import {
  Alert,
  Badge,
  Button,
  EmptyState,
  Field,
  Input,
  PageHeader,
  Panel,
  PanelBody,
  SectionLabel,
  Segmented,
  Select,
} from '../ui/index.js';

const PAGE_SIZE = 30;

const SOURCE_LABELS: Readonly<Record<string, string>> = {
  cocooncenter: 'Cocoon Center (FR)',
  pharmaciedesdrakkars: 'Pharmacie des Drakkars (FR)',
  pharmagdd: 'Pharma GDD (FR)',
  boticinal: 'Boticinal (FR)',
};

/** Render a contenance the way the catalogue writes it. */
function formatContenance(value: number | null, unit: string | null, pack: number): string {
  if (value === null || unit === null) return '—';
  const unitLabels: Readonly<Record<string, string>> = {
    ml: 'ml',
    g: 'g',
    comprime: 'comprimés',
    gelule: 'gélules',
    capsule: 'capsules',
    sachet: 'sachets',
    ampoule: 'ampoules',
    piece: 'pièces',
    ml_ampoule: 'ml',
    piece_ml: 'pièces',
  };
  const shown = Number.isInteger(value) ? String(value) : String(value);
  const label = unitLabels[unit] ?? unit;
  return pack > 1 ? `${pack} x ${shown} ${label}` : `${shown} ${label}`;
}

function DiffBadges({ row }: { readonly row: EnrichmentProposal }): React.ReactElement {
  const { diff } = row;
  const any = diff.nameChanged || diff.brandChanged || diff.contChanged || diff.gtinChanged;
  if (!any) return <span className="text-xs text-ink-muted">identique</span>;
  return (
    <span className="flex flex-wrap gap-1">
      {diff.nameChanged ? <Badge tone="scan">nom</Badge> : null}
      {diff.brandChanged ? <Badge tone="neutral">marque</Badge> : null}
      {diff.contChanged ? (
        <Badge tone="scan">contenance</Badge>
      ) : diff.contMissing ? (
        <Badge tone="neutral">contenance absente</Badge>
      ) : null}
      {diff.gtinChanged ? <Badge tone="scan">GTIN</Badge> : null}
    </span>
  );
}

type PillState = 'match' | 'differ' | 'missing';

function pillTone(state: PillState): 'good' | 'scan' | 'neutral' {
  return state === 'match' ? 'good' : state === 'differ' ? 'scan' : 'neutral';
}

/** Plain text from any HTML blob — the short description carries no formatting. */
function stripHtml(text: string): string {
  return text
    .replace(/<[^>]*>/g, ' ')
    .replace(/&[a-z]+;/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/** Sentence-aware excerpt (mirrors `smartExcerpt` in `@mapara/connectors`):
 * at most `maxLen` chars, preferably ending on a sentence boundary. */
function smartExcerpt(text: string, maxLen = 400): string {
  const clean = text.replace(/\s+/g, ' ').trim();
  if (clean.length <= maxLen) return clean;
  const slice = clean.slice(0, maxLen);
  let cut = -1;
  for (const m of slice.matchAll(/[.!?…](?=\s|$)/g)) cut = (m.index ?? -1) + 1;
  if (cut >= Math.floor(maxLen * 0.6)) return slice.slice(0, cut).trim();
  const space = slice.lastIndexOf(' ');
  const hard = space > maxLen * 0.6 ? slice.slice(0, space) : slice;
  return `${hard.trim()} …`;
}

/** Rich short text from a stored description: whole blocks with their
 * formatting kept (falls back to plain text when provided). */
function richShort(description: string | null, fallback: string | null): string {
  if (description === null || description.trim() === '') return fallback ?? '';
  const out = richExcerpt(sanitizeDescriptionHtml(description), 400);
  if (out !== '') return out;
  return fallback ?? '';
}

/** Rich excerpt of a sanitized description (mirrors `richExcerpt` in
 * `@mapara/connectors`): whole blocks with their formatting kept. */
function richExcerpt(sanitizedHtml: string, maxLen = 400): string {
  const blocks = [...sanitizedHtml.matchAll(/<(p|li|blockquote)>([\s\S]*?)<\/\1>/gi)].map(
    (m) => m[0],
  );
  if (blocks.length === 0) {
    const plain = stripHtml(sanitizedHtml);
    return plain === '' ? '' : `<p>${smartExcerpt(plain, maxLen)}</p>`;
  }
  const kept: string[] = [];
  let len = 0;
  for (const block of blocks) {
    const blockLen = stripHtml(block).length;
    if (kept.length > 0 && len + blockLen > maxLen) break;
    kept.push(block.trim());
    len += blockLen;
  }
  if (kept.length === 0) {
    return `<p>${smartExcerpt(stripHtml(blocks[0] ?? ''), maxLen)}</p>`;
  }
  return kept.join('\n');
}

/** Tags kept in long descriptions (Woo renders HTML): paragraphs, line
 * breaks, inline formatting and lists. No headings allowed — transformed to bold paragraphs. */
const DESC_ALLOWED_TAGS = new Set([
  'p',
  'br',
  'b',
  'strong',
  'i',
  'em',
  'u',
  'ul',
  'ol',
  'li',
  'blockquote',
]);

/** Source-faithful long description: keep <p>/<b>/lists, drop
 * <div>/<span>/<style>/… wrappers (attributes always stripped). Plain-text
 * sources (JSON-LD `\n\n`) are wrapped in <p>…</p> with <br> line breaks.
 * Transforms any <hx> tag to bold (<p><strong>...</strong></p>) — no h1, h2, h3 in descriptions. */
function sanitizeDescriptionHtml(raw: string): string {
  const input = raw.replace(/\r\n?/g, '\n');
  if (!/<[a-zA-Z][^>]*>/.test(input)) {
    const escaped = input.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    return escaped
      .split(/\n\s*\n/)
      .map((para) => para.trim())
      .filter((para) => para !== '')
      .map((para) => `<p>${para.replace(/\n/g, '<br>')}</p>`)
      .join('\n');
  }
  const noJunk = input
    .replace(/<!--[\s\S]*?-->/g, '')
    .replace(/<(script|style|iframe|object|embed)[^>]*>[\s\S]*?<\/\1>/gi, '')
    .replace(/&nbsp;/gi, ' ');
  const headingsToBold = noJunk
    .replace(/<h[1-6]\b[^>]*>([\s\S]*?)<\/h[1-6]>/gi, '<p><strong>$1</strong></p>')
    .replace(/<h[1-6]\b[^>]*>/gi, '<p><strong>')
    .replace(/<\/h[1-6]>/gi, '</strong></p>');
  const filtered = headingsToBold.replace(/<\/?([a-zA-Z][a-zA-Z0-9]*)\b[^>]*>/g, (m: string, tag: string) => {
    const t = tag.toLowerCase();
    if (!DESC_ALLOWED_TAGS.has(t)) return '';
    if (m.startsWith('</')) return `</${t}>`;
    return t === 'br' ? '<br>' : `<${t}>`;
  });
  return filtered
    .replace(/<p>\s*<\/p>/gi, '')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

interface SourceImageOption {
  readonly source: string;
  readonly url: string;
}

/** A Woo-draft request: base values plus an async sibling-fiche loader. */
interface DraftRequest {
  readonly initial: CreateWooDraftInitialData;
  readonly subtitle: string;
  readonly loadMoreOptions?: () => Promise<WooDraftExtraOptions | null>;
}

/** Unique options by value, preserving source order. */
function uniqOptions<T extends { readonly value: string }>(options: readonly T[]): T[] {
  const seen = new Set<string>();
  return options.filter((o) => {
    const key = o.value.trim();
    if (key === '' || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function uniqImageOptions(options: readonly SourceImageOption[]): SourceImageOption[] {
  const seen = new Set<string>();
  return options.filter((o) => {
    const key = o.url.trim();
    if (key === '' || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

/** Packshot + gallery options of one fiche: main first, then gallery extras. */
function ficheImageOptions(
  label: string,
  main: string | null,
  gallery: readonly string[] | undefined,
): SourceImageOption[] {
  const opts: SourceImageOption[] = [];
  if (main !== null && main.trim() !== '') opts.push({ source: label, url: main });
  (gallery ?? [])
    .filter((u) => u.trim() !== '' && u !== main)
    .forEach((url, i) => opts.push({ source: `${label} · galerie ${i + 1}`, url }));
  return opts;
}

export function EnrichmentPage(): React.ReactElement {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(0);
  const [status, setStatus] = useState<string>('proposed');
  const [sourceKey, setSourceKey] = useState<string>('all');
  const [onlyChangedName, setOnlyChangedName] = useState(false);
  const [onlyMissingContenance, setOnlyMissingContenance] = useState(false);
  const [search, setSearch] = useState('');
  const [drafts, setDrafts] = useState<Record<number, { name: string; cont: string }>>({});
  const [message, setMessage] = useState<{ text: string; tone: 'info' | 'alarm' } | null>(null);

  // Zoom & draft modals (the draft form itself lives in the shared
  // CreateWooDraftModal component — here only the request is held).
  const [zoomItem, setZoomItem] = useState<EnrichmentProposal | null>(null);
  const [draftRequest, setDraftRequest] = useState<DraftRequest | null>(null);
  const [createdDraftFeedback, setCreatedDraftFeedback] = useState<{
    wcProductId: number;
    name: string;
    ean: string | null;
    editUrl: string;
  } | null>(null);

  // Esc key & body scroll-lock for modals
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setZoomItem(null);
        setDraftRequest(null);
      }
    };
    if (zoomItem !== null || draftRequest !== null) {
      window.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [zoomItem, draftRequest]);

  const listQuery = useQuery({
    queryKey: ['enrichment', page, status, sourceKey, onlyChangedName, onlyMissingContenance, search],
    queryFn: () =>
      fetchEnrichmentProposals({
        status,
        ...(sourceKey === 'all' ? {} : { sourceKey }),
        onlyChangedName,
        onlyMissingContenance,
        ...(search === '' ? {} : { search }),
        limit: PAGE_SIZE,
        skip: page * PAGE_SIZE,
      }),
  });

  const decideMutation = useMutation({
    mutationFn: (input: { id: number; decision: 'accept' | 'reject'; draft?: { name: string; cont: string } }) => {
      const base: Parameters<typeof decideEnrichment>[1] = { decision: input.decision };
      const draft = input.draft;
      if (input.decision === 'accept' && draft !== undefined) {
        if (draft.name.trim() !== '') base.name = draft.name.trim();
        const m = /^(\d+(?:[.,]\d+)?)(?:\s*x\s*(\d+(?:[.,]\d+)?))?\s+(.+)$/.exec(draft.cont.trim());
        if (m !== null) {
          const packRaw = m[2];
          const valueRaw = m[1];
          const unitRaw = m[3];
          if (valueRaw !== undefined) base.contenanceValue = Number(valueRaw.replace(',', '.'));
          if (packRaw !== undefined) base.contenancePack = Number(packRaw.replace(',', '.'));
          if (unitRaw !== undefined) base.contenanceUnit = unitRaw.trim().toLowerCase();
        }
      }
      return decideEnrichment(input.id, base);
    },
    onSuccess: () => {
      setMessage({ text: 'Décision enregistrée.', tone: 'info' });
      void queryClient.invalidateQueries({ queryKey: ['enrichment'] });
    },
    onError: (error: Error) => setMessage({ text: `Échec : ${error.message}`, tone: 'alarm' }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => deleteEnrichmentProposal(id),
    onSuccess: () => {
      setMessage({ text: 'Proposition supprimée.', tone: 'info' });
      void queryClient.invalidateQueries({ queryKey: ['enrichment'] });
    },
    onError: (error: Error) => setMessage({ text: `Échec de suppression : ${error.message}`, tone: 'alarm' }),
  });

  const [isCrawling, setIsCrawling] = useState(false);
  const [isLiveSearching, setIsLiveSearching] = useState(false);
  const [liveLookupFeedback, setLiveLookupFeedback] = useState<LiveLookupResult | null>(null);

  const handleTriggerCrawl = async (full = false) => {
    setIsCrawling(true);
    try {
      const crawlOptions = full ? { full: true, limit: 1000 } : { limit: 200 };
      if (sourceKey === 'cocooncenter') {
        const res = await triggerEnrichmentCrawl('cocooncenter', crawlOptions);
        setMessage({
          text: `✓ Crawl Cocoon Center ${full ? 'catalogue complet (progressif) ' : ''}mis en file avec succès (job ${res.jobId ?? '—'}).`,
          tone: 'info',
        });
      } else if (sourceKey === 'pharmaciedesdrakkars') {
        const res = await triggerEnrichmentCrawl('pharmaciedesdrakkars', crawlOptions);
        setMessage({
          text: `✓ Crawl Pharmacie des Drakkars ${full ? 'catalogue complet (progressif) ' : ''}mis en file avec succès (job ${res.jobId ?? '—'}).`,
          tone: 'info',
        });
      } else if (sourceKey === 'pharmagdd') {
        const res = await triggerEnrichmentCrawl('pharmagdd', crawlOptions);
        setMessage({
          text: `✓ Crawl Pharma GDD ${full ? 'catalogue complet (progressif) ' : ''}mis en file avec succès (job ${res.jobId ?? '—'}).`,
          tone: 'info',
        });
      } else if (sourceKey === 'boticinal') {
        const res = await triggerEnrichmentCrawl('boticinal', crawlOptions);
        setMessage({
          text: `✓ Crawl Boticinal ${full ? 'catalogue complet (progressif) ' : ''}mis en file avec succès (job ${res.jobId ?? '—'}).`,
          tone: 'info',
        });
      } else {
        const [resCocoon, resDrakkars, resGdd, resBoticinal] = await Promise.all([
          triggerEnrichmentCrawl('cocooncenter', crawlOptions),
          triggerEnrichmentCrawl('pharmaciedesdrakkars', crawlOptions),
          triggerEnrichmentCrawl('pharmagdd', crawlOptions),
          triggerEnrichmentCrawl('boticinal', crawlOptions),
        ]);
        setMessage({
          text: `✓ Crawls ${full ? 'complets ' : ''}mis en file : Cocoon Center (job ${resCocoon.jobId ?? '—'}), Pharmacie des Drakkars (job ${resDrakkars.jobId ?? '—'}), Pharma GDD (job ${resGdd.jobId ?? '—'}) et Boticinal (job ${resBoticinal.jobId ?? '—'}).`,
          tone: 'info',
        });
      }
    } catch (error: unknown) {
      setMessage({
        text: `Échec du lancement des crawls : ${error instanceof Error ? error.message : String(error)}`,
        tone: 'alarm',
      });
    } finally {
      setIsCrawling(false);
    }
  };

  const handleLiveLookup = async (lookupQuery?: string) => {
    const q = (lookupQuery ?? search).trim();
    if (!q) {
      setMessage({ text: 'Veuillez saisir un code EAN ou un nom de produit à rechercher.', tone: 'alarm' });
      return;
    }
    setIsLiveSearching(true);
    setLiveLookupFeedback(null);
    try {
      const res = await liveLookupEnrichment(q, sourceKey === 'all' ? undefined : sourceKey);
      setLiveLookupFeedback(res);
      if (res.savedProposals.length > 0) {
        setMessage({
          text: `✓ ${res.savedProposals.length} proposition(s) récupérée(s) en direct et enregistrée(s) !`,
          tone: 'info',
        });
        void queryClient.invalidateQueries({ queryKey: ['enrichment'] });
      } else if (res.unlinkedRecords.length > 0) {
        setMessage({
          text: `Produit trouvé sur les sites français (${res.unlinkedRecords.length} fiche(s)), mais aucun produit local ne correspond encore. Vous pouvez créer le brouillon WooCommerce ci-dessous.`,
          tone: 'info',
        });
      } else {
        setMessage({
          text: res.message || 'Aucun produit trouvé sur Cocoon Center, Pharmacie des Drakkars, Pharma GDD ou Boticinal.',
          tone: 'alarm',
        });
      }
    } catch (err) {
      setMessage({
        text: `Échec de la recherche en direct : ${err instanceof Error ? err.message : String(err)}`,
        tone: 'alarm',
      });
    } finally {
      setIsLiveSearching(false);
    }
  };

  const rows = listQuery.data?.items ?? [];
  const total = listQuery.data?.total ?? 0;
  const maxPage = Math.max(0, Math.ceil(total / PAGE_SIZE) - 1);

  const searchDigits = search.replace(/\D/g, '');
  const isBarcodeSearch = searchDigits.length >= 8;

  // Helper to open the shared draft modal from a proposal
  const openDraftModalFromProposal = (row: EnrichmentProposal) => {
    const name = toTitleCase(drafts[row.id]?.name || row.proposal.name || row.product.name);
    const rawBrand = row.proposal.brand || row.product.brand || '';
    const brand = rawBrand ? toTitleCase(rawBrand) : '';
    const ean = row.proposal.gtin || row.product.gtinCanonical || row.product.barcodeRaw || row.product.eanRaw || '';
    const cont = drafts[row.id]?.cont || formatContenance(row.proposal.contenanceValue, row.proposal.contenanceUnit, row.proposal.contenancePack);
    const form = row.proposal.form || '';
    const presentation = row.proposal.presentation || '';
    const image = row.proposal.image || row.product.image || '';
    const sourceDesc = row.proposal.description || '';

    // Description courte : le texte SOURCE, sans aucun formatage HTML,
    // coupé à la fin d'une phrase (jamais en plein mot).
    const shortDesc =
      richShort(
        sourceDesc,
        `${name}${brand !== '' ? ` — ${brand}` : ''}${cont !== '' && cont !== '—' ? ` (${cont})` : ''}`,
      );

    // Description longue : la mise en forme SOURCE (paragraphes, gras,
    // listes), wrappers et styles nettoyés.
    const longDesc =
      sourceDesc.trim() !== ''
        ? sanitizeDescriptionHtml(sourceDesc)
        : `<p><strong>${name}</strong>${brand ? ` de la marque ${brand}` : ''}${cont && cont !== '—' ? ` (${cont})` : ''}.</p>\n` +
          `<p><strong>Caractéristiques du produit :</strong></p>\n<ul>\n` +
          (brand ? `  <li><strong>Marque :</strong> ${brand}</li>\n` : '') +
          (cont && cont !== '—' ? `  <li><strong>Contenance :</strong> ${cont}</li>\n` : '') +
          (form ? `  <li><strong>Forme :</strong> ${form}</li>\n` : '') +
          (presentation ? `  <li><strong>Présentation :</strong> ${presentation}</li>\n` : '') +
          (ean ? `  <li><strong>Code-barres (EAN) :</strong> ${ean}</li>\n` : '') +
          `</ul>`;

    const rowLabel = SOURCE_LABELS[row.sourceKey] ?? row.sourceKey;
    const baseShort = richShort(sourceDesc, null);
    setDraftRequest({
      initial: {
        proposalId: row.id,
        productId: row.product.id,
        name,
        ean,
        brand,
        contenance: cont !== '—' ? cont : '',
        imageUrl: image,
        shortDescription: shortDesc,
        description: longDesc,
        nameOptions: [
          { source: 'Notre catalogue', value: toTitleCase(row.product.name) },
          { source: rowLabel, value: toTitleCase(row.proposal.name ?? '') },
        ],
        shortOptions: baseShort !== '' ? [{ source: rowLabel, value: baseShort }] : [],
        longOptions:
          sourceDesc.trim() !== '' ? [{ source: rowLabel, value: sanitizeDescriptionHtml(sourceDesc) }] : [],
        imageOptions: uniqImageOptions([
          ...ficheImageOptions('Notre catalogue', row.product.image, []),
          ...ficheImageOptions(rowLabel, row.proposal.image, row.proposal.gallery),
        ]),
        categoryHint: row.proposal.category,
      },
      subtitle: `Enrichissement · ${rowLabel} · proposition #${row.id}`,
      loadMoreOptions: () =>
        fetchEnrichmentProductProposals(row.product.id)
          .then((res) => {
            const sibs = res.proposals.filter((p) => p.id !== row.id);
            const extra: WooDraftExtraOptions = {
              nameOptions: [
                { source: 'Notre catalogue', value: toTitleCase(res.product.name) },
                ...sibs.map((p) => ({
                  source: SOURCE_LABELS[p.sourceKey] ?? p.sourceKey,
                  value: toTitleCase(p.name ?? ''),
                })),
              ],
              shortOptions: sibs.map((p) => ({
                source: SOURCE_LABELS[p.sourceKey] ?? p.sourceKey,
                value: richShort(p.description, p.shortDescription),
              })),
              longOptions: sibs.map((p) => ({
                source: SOURCE_LABELS[p.sourceKey] ?? p.sourceKey,
                value:
                  p.description && p.description.trim() !== '' ? sanitizeDescriptionHtml(p.description) : '',
              })),
              imageOptions: [
                ...ficheImageOptions('Notre catalogue', res.product.image, []),
                ...sibs.flatMap((p) =>
                  ficheImageOptions(SOURCE_LABELS[p.sourceKey] ?? p.sourceKey, p.image, p.gallery),
                ),
              ],
              categoryHint:
                sibs.map((p) => p.category).find((c): c is string => typeof c === 'string' && c.trim() !== '') ??
                null,
            };
            return extra;
          })
          .catch(() => null),
    });
  };

  // Helper to open the shared draft modal from a custom barcode search
  const openDraftModalFromBarcode = (customBarcode: string) => {
    const cleanEan = customBarcode.replace(/\D/g, '');
    const shortDesc = `Nouveau produit — Code EAN : ${cleanEan}.`;
    const longDesc =
      `<p><strong>Nouveau produit</strong>.</p>\n` +
      `<p><strong>Caractéristiques du produit :</strong></p>\n<ul>\n` +
      `  <li><strong>Code-barres (EAN) :</strong> ${cleanEan}</li>\n` +
      `</ul>`;

    setDraftRequest({
      initial: {
        ean: cleanEan,
        shortDescription: shortDesc,
        description: longDesc,
      },
      subtitle: `Nouveau produit · EAN ${cleanEan}`,
    });
  };

  // Helper to open the shared draft modal from an unlinked live lookup record
  const openDraftModalFromUnlinked = (rec: LiveLookupUnlinkedRecord) => {
    const name = toTitleCase(rec.proposedName || '');
    const rawBrand = rec.proposedBrand || '';
    const brand = rawBrand ? toTitleCase(rawBrand) : '';
    const ean = rec.proposedGtin || '';
    const cont = rec.contenance?.raw || '';
    const form = rec.form || '';
    const presentation = rec.presentation || '';
    const image = rec.imageUrl || '';
    const sourceDesc = rec.description || '';

    const shortDesc = richShort(
      sourceDesc,
      `${name}${brand !== '' ? ` — ${brand}` : ''}${cont !== '' ? ` (${cont})` : ''}`,
    );

    const longDesc =
      sourceDesc.trim() !== ''
        ? sanitizeDescriptionHtml(sourceDesc)
        : `<p><strong>${name}</strong>${brand ? ` de la marque ${brand}` : ''}${cont ? ` (${cont})` : ''}.</p>\n` +
          `<p><strong>Caractéristiques du produit :</strong></p>\n<ul>\n` +
          (brand ? `  <li><strong>Marque :</strong> ${brand}</li>\n` : '') +
          (cont ? `  <li><strong>Contenance :</strong> ${cont}</li>\n` : '') +
          (form ? `  <li><strong>Forme :</strong> ${form}</li>\n` : '') +
          (presentation ? `  <li><strong>Présentation :</strong> ${presentation}</li>\n` : '') +
          (ean ? `  <li><strong>Code-barres (EAN) :</strong> ${ean}</li>\n` : '') +
          `</ul>`;

    // Sibling fiches found in the same live search (same EAN when known)
    // feed the "which website" pills.
    const sibs = (liveLookupFeedback?.unlinkedRecords ?? []).filter(
      (r) => r.sourceUrl !== rec.sourceUrl && (ean === '' || r.proposedGtin === ean),
    );
    const recLabel = SOURCE_LABELS[rec.sourceKey] ?? rec.sourceKey;
    const baseShort = richShort(sourceDesc, null);

    setDraftRequest({
      initial: {
        name,
        ean,
        brand,
        contenance: cont,
        imageUrl: image,
        shortDescription: shortDesc,
        description: longDesc,
        nameOptions: uniqOptions([
          { source: recLabel, value: toTitleCase(rec.proposedName ?? '') },
          ...sibs.map((r) => ({
            source: SOURCE_LABELS[r.sourceKey] ?? r.sourceKey,
            value: toTitleCase(r.proposedName ?? ''),
          })),
        ]),
        shortOptions: uniqOptions([
          ...(baseShort !== '' ? [{ source: recLabel, value: baseShort }] : []),
          ...sibs.flatMap((r) => {
            const base = richShort(r.description ?? null, null);
            return base !== '' ? [{ source: SOURCE_LABELS[r.sourceKey] ?? r.sourceKey, value: base }] : [];
          }),
        ]),
        longOptions: uniqOptions([
          ...(sourceDesc.trim() !== '' ? [{ source: recLabel, value: sanitizeDescriptionHtml(sourceDesc) }] : []),
          ...sibs.flatMap((r) =>
            r.description && r.description.trim() !== ''
              ? [{ source: SOURCE_LABELS[r.sourceKey] ?? r.sourceKey, value: sanitizeDescriptionHtml(r.description) }]
              : [],
          ),
        ]),
        imageOptions: uniqImageOptions([
          ...ficheImageOptions(recLabel, image !== '' ? image : null, rec.images),
          ...sibs.flatMap((r) =>
            ficheImageOptions(SOURCE_LABELS[r.sourceKey] ?? r.sourceKey, r.imageUrl ?? null, r.images),
          ),
        ]),
        categoryHint:
          rec.category ??
          sibs.map((r) => r.category).find((c): c is string => typeof c === 'string' && c.trim() !== '') ??
          null,
      },
      subtitle: `Recherche en direct · ${recLabel}`,
    });
  };

  return (
    <>
      <PageHeader
        index="S21"
        title="Enrichissement Produits"
        purpose="Contrôle visuel HD, correction de nom/contenance/GTIN et création directe de brouillons WooCommerce à partir des catalogues de référence."
        meta={[
          { label: 'À traiter', value: String(total) },
          { label: 'Source', value: SOURCE_LABELS[sourceKey] ?? 'Toutes' },
        ]}
      />

      <div className="grid gap-4">
        {/* Top Controls Panel */}
        <Panel>
          <PanelBody className="flex flex-wrap items-end gap-3">
            <Field label="Statut">
              <Select value={status} onChange={(e) => { setStatus(e.target.value); setPage(0); }}>
                <option value="proposed">À traiter</option>
                <option value="accepted">Acceptés</option>
                <option value="rejected">Rejetés</option>
              </Select>
            </Field>

            <Field label="Source">
              <Select value={sourceKey} onChange={(e) => { setSourceKey(e.target.value); setPage(0); }}>
                <option value="all">Toutes les sources</option>
                <option value="cocooncenter">Cocoon Center (FR)</option>
                <option value="pharmaciedesdrakkars">Pharmacie des Drakkars (FR)</option>
                <option value="pharmagdd">Pharma GDD (FR)</option>
                <option value="boticinal">Boticinal (FR)</option>
              </Select>
            </Field>

            <div className="flex-1 min-w-[280px]">
              <Field label="Recherche (Code EAN, Nom ou SKU)">
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-ink-ghost pointer-events-none" />
                    <Input
                      className="pl-9 pr-8"
                      value={search}
                      onChange={(e) => { setSearch(e.target.value); setPage(0); }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && search.trim()) {
                          void handleLiveLookup();
                        }
                      }}
                      placeholder="ex : 3337875899543, CeraVe Sérum Rétinol..."
                    />
                    {search !== '' && (
                      <button
                        type="button"
                        onClick={() => { setSearch(''); setPage(0); }}
                        className="absolute right-2.5 top-1/2 -translate-y-1/2 text-ink-ghost hover:text-ink p-0.5 rounded cursor-pointer"
                        title="Effacer la recherche"
                      >
                        <X size={14} />
                      </button>
                    )}
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => void handleLiveLookup()}
                    disabled={isLiveSearching || !search.trim()}
                    title="Interroger instantanément Cocooncenter, Drakkars, Pharma GDD et Boticinal pour cette référence"
                    className="shrink-0"
                  >
                    <Zap size={14} className={isLiveSearching ? 'animate-spin text-amber-500' : 'text-amber-500'} />{' '}
                    {isLiveSearching ? 'Scraping direct…' : 'Rechercher en direct (FR)'}
                  </Button>
                </div>
              </Field>
            </div>

            <Segmented<'all' | 'names' | 'cont'>
              ariaLabel="Filtres de révision"
              options={[
                { value: 'all', label: 'Tous' },
                { value: 'names', label: 'Nom différent' },
                { value: 'cont', label: 'Contenance absente' },
              ]}
              value={onlyChangedName ? 'names' : onlyMissingContenance ? 'cont' : 'all'}
              onChange={(value: 'all' | 'names' | 'cont') => {
                setOnlyChangedName(value === 'names');
                setOnlyMissingContenance(value === 'cont');
                setPage(0);
              }}
            />

            <div className="flex items-center gap-2">
              <Button
                onClick={() => void handleTriggerCrawl(false)}
                disabled={isCrawling}
                title="Lancer un crawl standard (200 URLs)"
              >
                <RefreshCw size={14} className={isCrawling ? 'animate-spin' : ''} />{' '}
                {sourceKey === 'cocooncenter'
                  ? 'Crawl Cocoon (200)'
                  : sourceKey === 'pharmaciedesdrakkars'
                    ? 'Crawl Drakkars (200)'
                    : sourceKey === 'pharmagdd'
                      ? 'Crawl Pharma GDD (200)'
                      : sourceKey === 'boticinal'
                        ? 'Crawl Boticinal (200)'
                        : 'Crawl standard'}
              </Button>
              <Button
                variant="outline"
                onClick={() => void handleTriggerCrawl(true)}
                disabled={isCrawling}
                title="Parcours continu du catalogue avec sauvegarde du curseur dans la base de données"
              >
                <Globe size={14} /> Crawl catalogue complet
              </Button>
            </div>
          </PanelBody>
        </Panel>

        {/* Barcode Search Alert Banner if digits entered */}
        {isBarcodeSearch && (
          <div className="rounded-2xl border border-herb-600/30 bg-herb-50/50 p-3.5 flex flex-wrap items-center justify-between gap-3 shadow-2xs">
            <div className="flex items-center gap-2">
              <span className="rounded-full bg-herb-700 text-white p-1">
                <Sparkles size={14} />
              </span>
              <div>
                <span className="text-xs font-bold text-ink">Recherche EAN active : </span>
                <span className="font-mono text-xs font-bold text-herb-900 bg-herb-100 px-2 py-0.5 rounded border border-herb-300">
                  {searchDigits}
                </span>
                <span className="text-xs text-ink-soft ml-2">
                  ({rows.length} proposition(s) d'enrichissement — Cocoon Center / Drakkars / Pharma GDD / Boticinal)
                </span>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => void handleLiveLookup(searchDigits)}
                disabled={isLiveSearching}
                className="shadow-2xs font-bold"
              >
                <Zap size={13} className={isLiveSearching ? 'animate-spin' : 'text-amber-500'} /> Scraper en direct (FR)
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={() => openDraftModalFromBarcode(searchDigits)}
                className="shadow-2xs font-bold"
              >
                <ShoppingBag size={14} /> Créer brouillon Woo (EAN {searchDigits})
              </Button>
            </div>
          </div>
        )}

        {/* Live Search Suggestion Card when search has 0 results */}
        {search.trim() !== '' && rows.length === 0 && (
          <div className="rounded-2xl border border-herb-300 bg-gradient-to-r from-herb-50/70 via-white to-cream-50/80 p-4 shadow-xs flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
            <div className="flex items-start gap-3">
              <div className="rounded-xl bg-herb-700 text-white p-2.5 shrink-0 shadow-2xs">
                <Globe className="h-5 w-5" />
              </div>
              <div>
                <h4 className="text-sm font-bold text-ink">
                  Produit non présent dans les propositions locales
                </h4>
                <p className="text-xs text-ink-soft mt-0.5 leading-relaxed">
                  Ce produit n'a pas encore été synchronisé dans votre base locale. Cliquez pour interroger en direct <strong>Cocoon Center</strong>, <strong>Pharmacie des Drakkars</strong>, <strong>Pharma GDD</strong> et <strong>Boticinal</strong> afin de récupérer sa fiche complète, son code-barres EAN certifié, sa contenance et son visuel HD.
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0 w-full md:w-auto">
              <Button
                variant="primary"
                onClick={() => void handleLiveLookup()}
                disabled={isLiveSearching}
                className="w-full md:w-auto font-bold shadow-2xs"
              >
                <Zap size={14} className={isLiveSearching ? 'animate-spin' : ''} />
                {isLiveSearching ? 'Recherche en direct en cours…' : `Rechercher "${search.trim()}" en direct`}
              </Button>
            </div>
          </div>
        )}

        {/* Discovered French products outside local DB */}
        {liveLookupFeedback?.unlinkedRecords && liveLookupFeedback.unlinkedRecords.length > 0 && (
          <div className="rounded-2xl border border-amber-300 bg-amber-50/60 p-4 shadow-xs grid gap-3 animate-fade">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-amber-600" />
                <h4 className="text-sm font-bold text-ink">
                  {liveLookupFeedback.unlinkedRecords.length} fiche(s) trouvée(s) sur les sites français (hors catalogue local)
                </h4>
              </div>
              <Button size="sm" variant="ghost" onClick={() => setLiveLookupFeedback(null)}>
                Fermer
              </Button>
            </div>
            <p className="text-xs text-ink-soft">
              Ces produits existent chez nos confrères français mais aucun produit dans notre catalogue interne n'a encore cet EAN ou ce nom. Vous pouvez créer directement le brouillon WooCommerce pré-rempli en 1 clic :
            </p>
            <div className="grid gap-3 sm:grid-cols-2">
              {liveLookupFeedback.unlinkedRecords.map((rec, idx) => (
                <div key={idx} className="rounded-xl border border-amber-200 bg-card p-3.5 flex items-start gap-3 shadow-2xs">
                  {rec.imageUrl ? (
                    <img src={rec.imageUrl} alt={rec.proposedName ?? ''} className="h-16 w-16 object-contain rounded-lg border border-border shrink-0 bg-white p-1" />
                  ) : (
                    <div className="h-16 w-16 rounded-lg bg-surface flex items-center justify-center shrink-0 text-ink-ghost">
                      <Package size={24} />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-1 mb-1">
                      <span className="inline-flex items-center gap-1 rounded-full border border-sky-300 bg-sky-50 px-2 py-0.5 text-[11px] font-bold text-sky-900 shadow-2xs">
                        <Globe size={11} className="text-sky-600" />
                        {SOURCE_LABELS[rec.sourceKey] ?? rec.sourceKey}
                      </span>
                      {rec.proposedGtin ? (
                        <span className="text-[10px] font-mono font-bold text-herb-700 bg-herb-50 border border-herb-200 rounded px-1.5 py-0.5">
                          EAN : {rec.proposedGtin}
                        </span>
                      ) : null}
                    </div>
                    <p className="text-xs font-bold text-ink truncate">{rec.proposedName}</p>
                    <p className="text-xs text-ink-soft mt-0.5">
                      {rec.proposedBrand ? <span className="font-semibold text-ink">{rec.proposedBrand} • </span> : null}
                      {rec.contenance?.raw || 'Contenance non spécifiée'}
                    </p>
                    <div className="mt-2.5 flex items-center gap-2">
                      <Button
                        size="sm"
                        variant="primary"
                        onClick={() => openDraftModalFromUnlinked(rec)}
                        className="font-bold shadow-2xs"
                      >
                        <ShoppingBag size={13} /> Créer brouillon Woo
                      </Button>
                      <a
                        href={rec.sourceUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="text-xs text-ink-muted hover:text-ink inline-flex items-center gap-1 hover:underline ml-1"
                      >
                        Voir source <ExternalLink size={12} />
                      </a>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Global Feedback Alert */}
        {message !== null && (
          <Alert tone={message.tone === 'alarm' ? 'alarm' : 'info'}>
            <div className="flex items-center justify-between">
              <span>{message.text}</span>
              <button
                type="button"
                onClick={() => setMessage(null)}
                className="text-xs underline ml-2 cursor-pointer"
              >
                Fermer
              </button>
            </div>
          </Alert>
        )}

        {/* Success WooCommerce Draft Feedback Card */}
        {createdDraftFeedback !== null && (
          <div className="rounded-2xl border border-emerald-300 bg-emerald-50 p-4 shadow-sm flex flex-wrap items-center justify-between gap-3 animate-fade">
            <div className="flex items-center gap-3">
              <CheckCircle2 className="h-6 w-6 text-emerald-700 shrink-0" />
              <div>
                <h4 className="text-sm font-bold text-emerald-950">
                  Brouillon WooCommerce créé avec succès (ID #{createdDraftFeedback.wcProductId})
                </h4>
                <p className="text-xs text-emerald-900 mt-0.5">
                  Produit : <strong>{createdDraftFeedback.name}</strong>
                  {createdDraftFeedback.ean ? ` · EAN : ${createdDraftFeedback.ean}` : ''}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <a
                href={createdDraftFeedback.editUrl}
                target="_blank"
                rel="noreferrer noopener"
                className="inline-flex items-center gap-1.5 rounded-xl bg-emerald-700 hover:bg-emerald-800 text-white px-3.5 py-1.5 text-xs font-bold shadow-2xs transition-colors"
              >
                Ouvrir la fiche dans Woo <ExternalLink size={13} />
              </a>
              <button
                type="button"
                onClick={() => setCreatedDraftFeedback(null)}
                className="p-1 text-emerald-800 hover:text-emerald-950"
              >
                <X size={16} />
              </button>
            </div>
          </div>
        )}

        {/* Main List of Proposals */}
        {listQuery.isLoading ? (
          <div className="flex flex-col items-center justify-center py-20 rounded-2xl border border-border bg-card text-ink-soft">
            <RefreshCw className="h-8 w-8 animate-spin text-herb-700" />
            <p className="mt-3 text-sm font-medium">Chargement des fiches d'enrichissement & images HD...</p>
          </div>
        ) : rows.length === 0 ? (
          <div className="rounded-2xl border border-border bg-card p-10 text-center">
            <EmptyState
              title="Aucune proposition trouvée"
              hint={
                search !== ''
                  ? `Aucun produit ne correspond à la recherche "${search}".`
                  : 'Lancez un crawl ou modifiez vos filtres.'
              }
            />
            {isBarcodeSearch && (
              <div className="mt-4">
                <Button variant="primary" onClick={() => openDraftModalFromBarcode(searchDigits)}>
                  <ShoppingBag size={14} /> Créer un nouveau produit brouillon dans WooCommerce (EAN {searchDigits})
                </Button>
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            {rows.map((row) => {
              const draft = drafts[row.id] ?? {
                name: row.proposal.name ?? '',
                cont: formatContenance(
                  row.proposal.contenanceValue,
                  row.proposal.contenanceUnit,
                  row.proposal.contenancePack,
                ),
              };
              const setDraft = (next: { name: string; cont: string }) =>
                setDrafts((prev) => ({ ...prev, [row.id]: next }));

              const oursCont =
                row.product.sizeValue === null
                  ? null
                  : formatContenance(row.product.sizeValue, row.product.sizeUnit, 1);
              const propCont =
                row.proposal.contenanceValue === null
                  ? null
                  : formatContenance(
                      row.proposal.contenanceValue,
                      row.proposal.contenanceUnit,
                      row.proposal.contenancePack,
                    );
              const contState: PillState =
                oursCont === null || oursCont === '—' || propCont === null || propCont === '—'
                  ? 'missing'
                  : row.diff.contChanged
                    ? 'differ'
                    : 'match';

              const oursGtin =
                row.product.gtinCanonical ??
                row.product.barcodeRaw ??
                row.product.eanRaw ??
                null;
              const gtinState: PillState =
                oursGtin === null || row.proposal.gtin === null
                  ? 'missing'
                  : row.diff.gtinChanged
                    ? 'differ'
                    : 'match';

              return (
                <div
                  key={row.id}
                  className="rounded-2xl border border-border bg-card p-4 shadow-xs space-y-3 transition-shadow hover:shadow-sm"
                >
                  {/* Card Header Bar */}
                  <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border/60 pb-3">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-mono text-xs font-semibold text-ink-ghost">#{row.id}</span>
                      {row.product.sku && (
                        <span className="rounded bg-cream px-2 py-0.5 font-mono text-[11px] text-ink-soft border border-border/80">
                          SKU: {row.product.sku}
                        </span>
                      )}
                      {row.product.brand && (
                        <span className="rounded-full bg-herb-50 border border-herb-600/20 px-2.5 py-0.5 text-xs font-bold text-herb-800">
                          {row.product.brand}
                        </span>
                      )}
                      <span className="rounded-full bg-slate-100 text-slate-800 border border-slate-300 px-2 py-0.5 text-[11px] font-semibold">
                        Source : {SOURCE_LABELS[row.sourceKey] ?? row.sourceKey}
                      </span>
                      {row.status === 'accepted' ? (
                        <span className="rounded-full bg-emerald-100 border border-emerald-300 text-emerald-900 px-2.5 py-0.5 text-xs font-bold">
                          ✓ Accepté
                        </span>
                      ) : row.status === 'rejected' ? (
                        <span className="rounded-full bg-rose-100 border border-rose-300 text-rose-900 px-2.5 py-0.5 text-xs font-bold">
                          ✕ Rejeté
                        </span>
                      ) : (
                        <span className="rounded-full bg-amber-100 border border-amber-300 text-amber-900 px-2.5 py-0.5 text-xs font-bold">
                          À traiter
                        </span>
                      )}
                    </div>

                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => setZoomItem(row)}
                        className="inline-flex items-center gap-1 rounded-xl border border-border bg-cream/60 px-2.5 py-1 text-xs font-semibold text-ink-soft hover:bg-cream hover:text-ink transition-colors cursor-pointer"
                        title="Voir les packshots en grand format"
                      >
                        <Maximize2 className="h-3.5 w-3.5" />
                        Vue comparative HD
                      </button>

                      <Button
                        variant="primary"
                        size="sm"
                        onClick={() => openDraftModalFromProposal(row)}
                        className="shadow-2xs font-bold text-xs"
                        title="Créer un produit brouillon dans WooCommerce avec les données enrichies"
                      >
                        <ShoppingBag size={13} />
                        Créer brouillon Woo
                      </Button>

                      <button
                        type="button"
                        onClick={() => {
                          if (
                            window.confirm(
                              `Supprimer définitivement la proposition #${row.id} (${row.proposal.name ?? row.sourceKey}) ?`,
                            )
                          ) {
                            deleteMutation.mutate(row.id);
                          }
                        }}
                        disabled={deleteMutation.isPending}
                        className="inline-flex items-center gap-1 rounded-xl border border-border bg-card px-2.5 py-1 text-xs font-semibold text-ink-ghost hover:text-rose-700 hover:border-rose-300 transition-colors cursor-pointer"
                        title="Supprimer cette proposition (ex : fiche rattachée au mauvais produit)"
                      >
                        <X size={13} />
                        Supprimer
                      </button>
                    </div>
                  </div>

                  {/* Grid 3 Columns: Notre Produit / Proposition Source / Révision & Actions */}
                  <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
                    {/* 1. Notre Produit (Catalogue Actuel) */}
                    <div className="flex gap-4 rounded-xl border border-border/80 bg-paper/60 p-3">
                      {/* Big Packshot Ours */}
                      <div className="relative group shrink-0">
                        {row.product.image ? (
                          <img
                            src={row.product.image}
                            alt={row.product.name}
                            className="h-36 w-36 sm:h-40 sm:w-40 rounded-xl object-contain bg-white border border-border p-1 shadow-2xs"
                            loading="lazy"
                            onError={(e) => {
                              (e.target as HTMLElement).style.display = 'none';
                            }}
                          />
                        ) : (
                          <div className="flex h-36 w-36 sm:h-40 sm:w-40 items-center justify-center rounded-xl bg-card border border-border text-xs text-ink-ghost font-medium">
                            Sans image
                          </div>
                        )}
                        <button
                          type="button"
                          onClick={() => setZoomItem(row)}
                          className="absolute inset-0 flex items-center justify-center rounded-xl bg-ink/40 text-white opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                          title="Agrandir l'image"
                        >
                          <Eye className="h-5 w-5" />
                        </button>
                      </div>

                      {/* Details Ours */}
                      <div className="flex flex-1 flex-col justify-between min-w-0">
                        <div>
                          <span className="text-[10px] font-bold uppercase tracking-wider text-herb-700">
                            Notre Catalogue
                          </span>{' '}
                          {!row.product.isPublished && (
                            <span
                              className="rounded-full bg-slate-200 border border-slate-300 text-slate-700 px-2 py-0.5 text-[10px] font-bold"
                              title="Fiche présente dans la base mirror mais non publiée — invisible en boutique"
                            >
                              Non publié
                            </span>
                          )}
                          <h4 className="mt-0.5 text-sm font-bold text-ink leading-snug line-clamp-2" title={row.product.name}>
                            {row.product.name}
                          </h4>

                          <div className="mt-2 space-y-1">
                            <div className="text-xs text-ink-soft">
                              SKU : <span className="font-mono font-semibold text-ink">{row.product.sku}</span>
                              {row.product.brand && ` · ${row.product.brand}`}
                            </div>

                            <div className="mt-2 flex flex-wrap gap-1.5">
                              <Badge tone={pillTone(contState)}>
                                {oursCont === null || oursCont === '—' ? 'Contenance —' : oursCont}
                              </Badge>
                              <Badge tone={pillTone(gtinState)}>
                                {oursGtin === null ? 'GTIN —' : `GTIN ${oursGtin}`}
                              </Badge>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* 2. Proposition Source (Cocoon Center / Pharmacie des Drakkars) */}
                    <div className="flex gap-4 rounded-xl border border-border/80 bg-paper/60 p-3">
                      {/* Big Packshot Source */}
                      <div className="relative group shrink-0">
                        {row.proposal.image ? (
                          <img
                            src={row.proposal.image}
                            alt={row.proposal.name ?? ''}
                            className="h-36 w-36 sm:h-40 sm:w-40 rounded-xl object-contain bg-white border border-border p-1 shadow-2xs"
                            loading="lazy"
                            onError={(e) => {
                              (e.target as HTMLElement).style.display = 'none';
                            }}
                          />
                        ) : (
                          <div className="flex h-36 w-36 sm:h-40 sm:w-40 items-center justify-center rounded-xl bg-card border border-border text-xs text-ink-ghost font-medium">
                            Sans image
                          </div>
                        )}
                        <button
                          type="button"
                          onClick={() => setZoomItem(row)}
                          className="absolute inset-0 flex items-center justify-center rounded-xl bg-ink/40 text-white opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                          title="Agrandir l'image"
                        >
                          <Eye className="h-5 w-5" />
                        </button>
                      </div>

                      {/* Details Source */}
                      <div className="flex flex-1 flex-col justify-between min-w-0">
                        <div>
                          <div className="flex items-center justify-between">
                            <span className="text-[10px] font-bold uppercase tracking-wider text-sky-700">
                              Proposition {SOURCE_LABELS[row.sourceKey] ?? row.sourceKey}
                            </span>
                            <a
                              href={row.sourceUrl}
                              target="_blank"
                              rel="noreferrer noopener"
                              className="text-[11px] font-semibold text-sky-800 hover:underline inline-flex items-center gap-1"
                              title="Ouvrir la page source"
                            >
                              Page <ExternalLink size={10} />
                            </a>
                          </div>

                          <h4 className="mt-0.5 text-sm font-bold text-ink leading-snug line-clamp-2" title={row.proposal.name ?? ''}>
                            {row.proposal.name ?? '—'}
                          </h4>

                          <div className="mt-1 text-xs text-ink-soft">
                            {row.proposal.brand ?? '—'}
                            {row.proposal.form ? ` · ${row.proposal.form}` : ''}
                            {row.proposal.presentation ? ` · ${row.proposal.presentation}` : ''}
                          </div>
                          {row.proposal.category && (
                            <div className="mt-1 text-[11px] text-ink-ghost" title="Rayon publié par la source (indicatif)">
                              ▸ {row.proposal.category}
                            </div>
                          )}

                          <div className="mt-2 flex flex-wrap gap-1.5">
                            <Badge tone={pillTone(contState)}>
                              {row.proposal.contenanceRaw ?? 'Contenance absente'}
                            </Badge>
                            <Badge tone={pillTone(gtinState)}>
                              GTIN {row.proposal.gtin ?? 'absent'}
                            </Badge>
                          </div>

                          {row.proposal.shortDescription && (
                            <p className="mt-2 text-[11px] text-ink-soft line-clamp-2 italic">
                              "{row.proposal.shortDescription.replace(/<[^>]+>/g, '')}"
                            </p>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* 3. Révision, Corrections & Décision — or creation when we don't hold the barcode */}
                    {oursGtin === null ? (
                      <div className="rounded-xl border border-herb-600/25 bg-herb-50/40 p-3 flex flex-col justify-between space-y-3">
                        <div>
                          <span className="text-[10px] font-bold uppercase tracking-wider text-herb-800">
                            Nouveau code-barres
                          </span>
                          <p className="mt-1.5 text-xs text-ink-soft leading-relaxed">
                            Ce code-barres n'existe pas encore dans votre catalogue — aucun
                            rapprochement à valider. Créez directement la fiche :
                          </p>
                        </div>
                        <Button
                          variant="primary"
                          size="sm"
                          onClick={() => openDraftModalFromProposal(row)}
                          className="shadow-2xs font-bold w-full"
                          title="Créer un produit brouillon dans WooCommerce avec les données de la source"
                        >
                          <ShoppingBag size={13} /> Créer brouillon Woo
                        </Button>
                      </div>
                    ) : (
                    <div className="rounded-xl border border-border/80 bg-paper/60 p-3 flex flex-col justify-between space-y-3">
                      <div>
                        <div className="flex items-center justify-between">
                          <span className="text-[10px] font-bold uppercase tracking-wider text-ink-ghost">
                            Écarts & Correction
                          </span>
                          <DiffBadges row={row} />
                        </div>

                        <div className="mt-2 space-y-1.5">
                          <div>
                            <label className="text-[11px] font-semibold text-ink-soft block mb-0.5">
                              Nom retenu :
                            </label>
                            <Input
                              value={draft.name}
                              onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                              placeholder="Nom retenu pour la fiche"
                              className="text-xs"
                            />
                          </div>

                          <div>
                            <label className="text-[11px] font-semibold text-ink-soft block mb-0.5">
                              Contenance retenue :
                            </label>
                            <Input
                              value={draft.cont}
                              onChange={(e) => setDraft({ ...draft, cont: e.target.value })}
                              placeholder="ex : 30 comprimés, 500 ml"
                              className="text-xs"
                            />
                          </div>
                        </div>
                      </div>

                      <div className="pt-2 border-t border-border/60 flex items-center justify-between gap-2">
                        <div className="flex gap-1.5">
                          <button
                            type="button"
                            onClick={() => decideMutation.mutate({ id: row.id, decision: 'accept', draft })}
                            disabled={decideMutation.isPending}
                            className="inline-flex items-center gap-1 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-1.5 text-xs font-bold shadow-2xs transition-colors cursor-pointer"
                            title="Accepter cette proposition"
                          >
                            <Check size={14} /> Accepter
                          </button>

                          <button
                            type="button"
                            onClick={() => decideMutation.mutate({ id: row.id, decision: 'reject' })}
                            disabled={decideMutation.isPending}
                            className="inline-flex items-center gap-1 rounded-xl border border-rose-300 bg-white hover:bg-rose-50 text-rose-700 px-3 py-1.5 text-xs font-bold shadow-2xs transition-colors cursor-pointer"
                            title="Rejeter la proposition"
                          >
                            <X size={14} /> Rejeter
                          </button>
                        </div>

                        <Button
                          size="sm"
                          variant="soft"
                          onClick={() => openDraftModalFromProposal(row)}
                          className="text-xs font-bold"
                          title="Créer un brouillon WooCommerce"
                        >
                          <ShoppingBag size={13} /> Brouillon Woo
                        </Button>
                      </div>
                    </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Pagination Bar */}
        <div className="flex items-center justify-between text-sm py-2">
          <SectionLabel>
            {total} proposition(s) · page {page + 1} / {maxPage + 1}
          </SectionLabel>
          <div className="flex gap-2">
            <Button disabled={page <= 0} onClick={() => setPage((p) => Math.max(0, p - 1))}>
              Précédent
            </Button>
            <Button disabled={page >= maxPage} onClick={() => setPage((p) => p + 1)}>
              Suivant
            </Button>
          </div>
        </div>

        {/* Info Explanatory Panel */}
        <Panel>
          <PanelBody className="text-xs text-ink-muted flex items-start gap-2">
            <Info className="h-4 w-4 shrink-0 text-herb-700 mt-0.5" />
            <div>
              <p>
                <strong>Module d'enrichissement & révision catalogue :</strong> Les sites de référence (Cocoon Center, Pharmacie des Drakkars, Pharma GDD, Boticinal) servent exclusivement à enrichir la présentation, les contenances, les images HD et les codes-barres.
                <strong>« Notre Catalogue »</strong> = votre fiche mirror WooCommerce à laquelle la proposition est rattachée (badge <strong>Non publié</strong> si elle est invisible en boutique).
                Si le code-barres n'existe pas encore chez vous, la carte passe en mode création : pas d'acceptation à valider, juste <strong>« Créer brouillon Woo »</strong>.
                Le bouton <strong>« Créer brouillon Woo »</strong> copie immédiatement le nom, l'EAN (champs <code>global_unique_id</code> et <code>alg_ean</code>), l'image et les descriptions courte et longue vers un nouveau produit au statut <strong>Brouillon</strong> dans votre boutique WooCommerce.
              </p>
            </div>
          </PanelBody>
        </Panel>
      </div>

      {/* --------------------------------------------------------------------- */}
      {/* 1. Modal: Vue Comparative HD (Portaled to document.body)               */}
      {/* --------------------------------------------------------------------- */}
      {zoomItem !== null &&
        typeof document !== 'undefined' &&
        createPortal(
          <div
            className="fixed inset-0 z-[999] flex items-center justify-center bg-black/80 backdrop-blur-xs p-4 sm:p-6 overflow-y-auto animate-fade"
            onClick={() => setZoomItem(null)}
          >
            <div
              role="dialog"
              aria-modal="true"
              aria-label="Vue comparative HD Packshots"
              className="relative w-full max-w-4xl rounded-3xl border border-border bg-card p-6 shadow-2xl max-h-[92vh] overflow-y-auto m-auto animate-rise"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                type="button"
                onClick={() => setZoomItem(null)}
                className="absolute right-4 top-4 rounded-full p-2 text-ink-ghost hover:bg-cream hover:text-ink transition-colors cursor-pointer"
                title="Fermer (Échap)"
              >
                <X className="h-5 w-5" />
              </button>

              <div>
                <span className="rounded-full bg-herb-100 text-herb-900 px-3 py-1 text-xs font-bold">
                  Contrôle Visuel HD Packshots
                </span>
                <h3 className="mt-2 text-lg font-bold text-ink">{zoomItem.proposal.name || zoomItem.product.name}</h3>
                <p className="text-xs text-ink-soft">
                  Comparez côte à côte le packshot de votre catalogue et la photo de la source de référence.
                </p>
              </div>

              {/* Side by side large packshots */}
              <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Ours */}
                <div className="rounded-2xl border border-border bg-paper/60 p-4 flex flex-col items-center text-center">
                  <span className="text-xs font-bold text-herb-800 uppercase tracking-wider mb-2">Notre Catalogue</span>
                  {zoomItem.product.image ? (
                    <img
                      src={zoomItem.product.image}
                      alt={zoomItem.product.name}
                      className="h-64 w-64 rounded-xl object-contain bg-white border border-border p-2 shadow-sm"
                    />
                  ) : (
                    <div className="h-64 w-64 rounded-xl bg-card border border-border flex items-center justify-center text-xs text-ink-ghost">
                      Sans image
                    </div>
                  )}
                  <h4 className="mt-3 text-sm font-bold text-ink line-clamp-2">{zoomItem.product.name}</h4>
                  <div className="mt-1 flex flex-wrap gap-1.5 justify-center">
                    {zoomItem.product.sku && <Badge tone="neutral">SKU {zoomItem.product.sku}</Badge>}
                    {zoomItem.product.gtinCanonical && <Badge tone="good">GTIN {zoomItem.product.gtinCanonical}</Badge>}
                  </div>
                </div>

                {/* Source */}
                <div className="rounded-2xl border border-sky-300 bg-sky-50/40 p-4 flex flex-col items-center text-center">
                  <span className="text-xs font-bold text-sky-800 uppercase tracking-wider mb-2">
                    {SOURCE_LABELS[zoomItem.sourceKey] ?? zoomItem.sourceKey}
                  </span>
                  {zoomItem.proposal.image ? (
                    <img
                      src={zoomItem.proposal.image}
                      alt={zoomItem.proposal.name ?? ''}
                      className="h-64 w-64 rounded-xl object-contain bg-white border border-border p-2 shadow-sm"
                    />
                  ) : (
                    <div className="h-64 w-64 rounded-xl bg-card border border-border flex items-center justify-center text-xs text-ink-ghost">
                      Sans image
                    </div>
                  )}
                  <h4 className="mt-3 text-sm font-bold text-ink line-clamp-2">{zoomItem.proposal.name ?? '—'}</h4>
                  <div className="mt-1 flex flex-wrap gap-1.5 justify-center">
                    {zoomItem.proposal.brand && <Badge tone="neutral">{zoomItem.proposal.brand}</Badge>}
                    {zoomItem.proposal.gtin && <Badge tone="scan">GTIN {zoomItem.proposal.gtin}</Badge>}
                    {zoomItem.proposal.contenanceRaw && <Badge tone="neutral">{zoomItem.proposal.contenanceRaw}</Badge>}
                  </div>
                </div>
              </div>

              {/* Descriptions & Details if available */}
              {zoomItem.proposal.description && (
                <div className="mt-4 rounded-xl border border-border bg-card p-3 text-xs text-ink-soft">
                  <span className="font-bold text-ink block mb-1">Description fournie par la source :</span>
                  <p className="line-clamp-4 italic">
                    {zoomItem.proposal.description.replace(/<[^>]+>/g, '')}
                  </p>
                </div>
              )}

              {/* Action Buttons Footer */}
              <div className="mt-6 pt-4 border-t border-border flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                  <Button
                    variant="primary"
                    onClick={() => {
                      const item = zoomItem;
                      setZoomItem(null);
                      openDraftModalFromProposal(item);
                    }}
                  >
                    <ShoppingBag size={14} /> Créer brouillon Woo
                  </Button>

                  <Button
                    variant="soft"
                    onClick={() => {
                      decideMutation.mutate({ id: zoomItem.id, decision: 'accept' });
                      setZoomItem(null);
                    }}
                  >
                    <Check size={14} /> Accepter la proposition
                  </Button>

                  <Button
                    variant="outline"
                    onClick={() => {
                      decideMutation.mutate({ id: zoomItem.id, decision: 'reject' });
                      setZoomItem(null);
                    }}
                  >
                    <X size={14} /> Rejeter
                  </Button>
                </div>

                <Button variant="soft" onClick={() => setZoomItem(null)}>
                  Fermer
                </Button>
              </div>
            </div>
          </div>,
          document.body,
        )}

      {/* Shared WooCommerce draft modal — the same component as market
          discovery and the barcode checker (source pills, sentence case,
          packshot picker with dimensions, gallery, category hint). */}
      <CreateWooDraftModal
        isOpen={draftRequest !== null}
        onClose={() => setDraftRequest(null)}
        initialData={draftRequest?.initial ?? null}
        subtitle={draftRequest?.subtitle}
        hideContenanceField
        loadMoreOptions={draftRequest?.loadMoreOptions}
        onSuccess={(data) => {
          setCreatedDraftFeedback({
            wcProductId: data.wcProductId,
            name: data.name,
            ean: data.ean,
            editUrl: data.editUrl,
          });
          setMessage({
            text: `Brouillon #${data.wcProductId} créé avec succès sur WooCommerce pour "${data.name}".`,
            tone: 'info',
          });
          void queryClient.invalidateQueries({ queryKey: ['enrichment'] });
        }}
      />
    </>
  );
}
